import { useRef } from 'react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FileText, Upload, X } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { useEmployerDocUpload, type EmployerDocKind } from '../AddEmployerModal/upload';
import type { UploadedDoc } from '../AddEmployerModal/types';

export function EmployerDocUploadField({
  label,
  kind,
  doc,
  onUploaded,
  onClear,
  isInvalid = false,
  isRequired = true,
  errorMessage,
  'data-testid': dataTestId,
}: {
  label: React.ReactNode;
  kind: EmployerDocKind;
  doc: UploadedDoc | null;
  onUploaded: (doc: UploadedDoc) => void;
  onClear: () => void;
  isInvalid?: boolean;
  isRequired?: boolean;
  errorMessage?: React.ReactNode;
  'data-testid': string;
}): React.ReactElement {
  const { uploadFile, isLoading } = useEmployerDocUpload(kind);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file == null) {
      return;
    }
    try {
      onUploaded(await uploadFile(file));
    } catch {
      // uploadFile already surfaces a toast on failure.
    }
  };

  return (
    <FormControl isRequired={isRequired} isInvalid={isInvalid}>
      <FormLabel>{label}</FormLabel>
      {doc != null ? (
        <Flex
          align="center"
          gap={2}
          w="full"
          h={10}
          px={3}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          bg="bg.surface"
        >
          <FileText size={iconSize.sm} />
          <Text flex="1" noOfLines={1} fontSize="sm">
            {doc.filename}
          </Text>
          <IconButton
            aria-label={t`Remove file`}
            icon={<X size={iconSize.sm} />}
            size="xs"
            variant="ghost"
            onClick={onClear}
          />
        </Flex>
      ) : (
        <Flex
          as="button"
          type="button"
          align="center"
          gap={2}
          w="full"
          h={10}
          px={3}
          borderWidth="1px"
          borderColor={isInvalid ? 'danger' : 'border.default'}
          borderRadius="md"
          bg="bg.surface"
          cursor="pointer"
          _hover={{ borderColor: isInvalid ? 'danger' : 'border.emphasis' }}
          onClick={() => inputRef.current?.click()}
          data-testid={dataTestId}
        >
          {isLoading ? <Spinner size="sm" /> : <Upload size={iconSize.sm} />}
          <Text flex="1" textAlign="left" fontSize="sm" color="fg.subtle">
            {isLoading ? <Trans>Uploading…</Trans> : <Trans>Upload file</Trans>}
          </Text>
        </Flex>
      )}
      <input ref={inputRef} type="file" accept="application/pdf" hidden onChange={handleChange} />
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
}
